import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import VantaLayout from './componets/vantalayout';

const FreeToolsDirectory = () => {
  const tools = [
    {
      id: 'protrader-visual-journal',
      title: 'ProTrader Visual Journal',
      description: 'Professional-grade trading journal EA with real-time dashboard, automatic screenshots, and Google Sheets integration.',
      features: [
        'Real-time P&L tracking',
        'Auto chart screenshots',
        'Telegram alerts',
        'Risk management tools'
      ],
      value: '$297',
      status: 'available',
      route: '/free-tools/protrader-journal',
      icon: '📊',
      category: 'Trading Journal'
    },
    {
      id: 'protrader-app',
      title: 'ProTrader Mobile App',
      description: 'Complete mobile trading companion with advanced analytics and portfolio management.',
      features: [
        'Mobile portfolio tracking',
        'Real-time notifications',
        'Advanced charting',
        'Trade management'
      ],
      value: '$197',
      status: 'available',
      route: '/free-tools/protrader-app',
      icon: '📱',
      category: 'Mobile Apps'
    },
    {
      id: 'honpropdesk',
      title: 'HonPropDesk Manager v3.3',
      description: 'Ultimate prop firm trading management system with advanced risk management and automated compliance.',
      features: [
        'Automated rule enforcement',
        'Real-time drawdown protection',
        'Telegram integration',
        'Professional interface'
      ],
      value: '$497',
      status: 'available',
      route: '/honpropdesk',
      icon: '🏢',
      category: 'Prop Firm Tools'
    },
    {
      id: 'hon-ahi-ea',
      title: 'Hon-Ashi EA',
      description: 'Advanced automated trading system with machine learning capabilities.',
      features: [
        'AI-powered signals',
        'Adaptive algorithms',
        'Market condition analysis',
        'Multi-timeframe trading'
      ],
      value: '$697',
      status: 'coming-soon',
      route: '/free-tools/hon-ahi-ea',
      icon: '🤖',
      category: 'Expert Advisors'
    }
  ];

  const categories = [...new Set(tools.map(tool => tool.category))];

  return (
    <VantaLayout>
      <div className="min-h-screen pt-20 pb-8 px-4 overflow-x-hidden relative">
        <div className="max-w-7xl mx-auto w-full">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-500 px-6 py-3 rounded-full text-white font-bold mb-6 text-lg">
              <span className="text-2xl">🎁</span>
              <span>FREE TRADING TOOLS</span>
            </div>

            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent font-orbitron">
              Professional Trading Tools
            </h1>

            <p className="text-base sm:text-lg lg:text-xl text-gray-300 mb-6 sm:mb-8 max-w-3xl mx-auto px-4 text-left" style={{textShadow: '0 1px 2px rgba(0,0,0,0.6)'}}>
              Get access to premium trading tools worth over $15,000 absolutely FREE when you sign up with one of our partner brokers.
            </p>

            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 sm:p-6 backdrop-blur-sm max-w-2xl mx-auto">
              <h3 className="text-base sm:text-lg font-semibold text-blue-400 mb-2">How It Works:</h3>
              <div className="text-gray-300 text-left space-y-2">
                <div className="flex items-center gap-3">
                  <span className="text-green-400">1️⃣</span>
                  <span>Choose your free tool below</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-green-400">2️⃣</span>
                  <span>Sign up with your preferred broker</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-green-400">3️⃣</span>
                  <span>Get instant access to your tool + setup support</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Tools Grid */}
          <div className="space-y-12">
            {categories.map((category, categoryIndex) => (
              <motion.div
                key={category}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: categoryIndex * 0.1 }}
              >
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-3">
                  <span className="w-1 h-6 sm:h-8 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full"></span>
                  {category}
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
                  {tools.filter(tool => tool.category === category).map((tool, toolIndex) => (
                    <motion.div
                      key={tool.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: (categoryIndex * 0.1) + (toolIndex * 0.1) }}
                      className="glass-card glass-card-large group hover:scale-105 transition-all duration-300"
                    >
                      {/* Tool Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <span className="text-3xl">{tool.icon}</span>
                          <div>
                            <h3 className="glass-card-title text-base sm:text-lg mb-1">{tool.title}</h3>
                            <div className="flex items-center gap-2">
                              <span className="text-emerald-400 font-semibold">FREE</span>
                              <span className="text-gray-400 line-through text-sm">Value: {tool.value}</span>
                            </div>
                          </div>
                        </div>

                        {tool.status === 'coming-soon' && (
                          <span className="bg-yellow-500/20 text-yellow-400 text-xs px-2 py-1 rounded-full">
                            Coming Soon
                          </span>
                        )}
                      </div>

                      {/* Description */}
                      <p className="glass-card-text text-xs sm:text-sm mb-4">
                        {tool.description}
                      </p>

                      {/* Features */}
                      <div className="space-y-2 mb-6">
                        {tool.features.map((feature, index) => (
                          <div key={index} className="flex items-center gap-2 text-gray-300 text-xs sm:text-sm">
                            <span className="text-green-400">✅</span>
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>

                      {/* CTA Button */}
                      {tool.status === 'available' ? (
                        <Link
                          to={tool.route}
                          className="glass-btn glass-btn-primary w-full text-center block group-hover:scale-105 transition-transform"
                        >
                          <span className="flex items-center justify-center gap-2">
                            <span>Get This Tool FREE</span>
                            <span className="text-lg">🚀</span>
                          </span>
                        </Link>
                      ) : (
                        <button
                          disabled
                          className="w-full py-3 px-6 rounded-lg bg-gray-600/50 text-gray-400 cursor-not-allowed"
                        >
                          Coming Soon - Join Waitlist
                        </button>
                      )}
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Footer CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-center mt-16 p-8 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl border border-blue-500/20 backdrop-blur-sm"
          >
            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-3 sm:mb-4 text-left">Want All Tools?</h3>
            <p className="text-sm sm:text-base lg:text-lg text-gray-300 mb-4 sm:mb-6 max-w-2xl mx-auto text-left">
              Get access to our complete professional trading toolkit when you open an account with any of our partner brokers.
            </p>

            <div className="flex flex-wrap justify-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-400">
              <span className="whitespace-nowrap">🏢 FBS Markets</span>
              <span className="whitespace-nowrap">🏢 Hantec Markets</span>
              <span className="whitespace-nowrap">🏢 Exness</span>
              <span className="whitespace-nowrap">🏢 IC Markets</span>
            </div>
          </motion.div>
        </div>
      </div>
    </VantaLayout>
  );
};

export default FreeToolsDirectory;